// bookie-onyx-preview — Onyx-black paper invitation card.
//
// Mood: black-tie, formal, milestone. The outer frame is a slick onyx
// gradient with a champagne-gold sheen line; the inner card sits on
// near-black paper with ivory ink. The focal element is a thin gold
// laurel-wreath medallion that frames the names. Eyebrow + bottom
// caption are roman small-caps in the template accent (champagne).
import { type LayoutCtx } from "./_shared";

export function BookieOnyxPreviewLayout({
  names,
  date,
  venue,
  accent,
  ink,
  bg,
  font,
}: LayoutCtx) {
  // Onyx foil — alternating jet black and warm graphite stops give
  // the border a believable polished-stone glint at thumbnail size.
  const FOIL =
    "linear-gradient(135deg, #050505 0%, #1A1A1A 25%, #2A2520 50%, #1A1A1A 78%, #050505 100%)";

  return (
    <div
      style={{
        position: "absolute",
        inset: 0,
        background: FOIL,
        padding: "5%",
        display: "flex",
      }}
    >
      <div
        style={{
          flex: 1,
          background: bg,
          color: ink,
          padding: "8%",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          textAlign: "center",
          boxShadow: `inset 0 0 0 1px ${accent}55`,
          position: "relative",
        }}
      >
        {/* Eyebrow */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 7,
            fontWeight: 700,
            letterSpacing: "0.4em",
            color: accent,
            marginBottom: "5%",
          }}
        >
          TAKLIFNOMA
        </div>

        {/* Laurel-wreath ornament — thin gold strokes, two halves
            mirroring around a tiny central diamond. */}
        <div
          style={{
            width: "62%",
            aspectRatio: "1 / 1",
            position: "relative",
            marginBottom: "5%",
          }}
        >
          <svg
            viewBox="0 0 120 120"
            style={{ width: "100%", height: "100%", display: "block" }}
            aria-hidden
          >
            {/* Outer hairline */}
            <circle cx="60" cy="60" r="56" fill="none" stroke={accent} strokeOpacity="0.45" strokeWidth="0.8" />
            <circle cx="60" cy="60" r="52" fill="none" stroke={accent} strokeOpacity="0.22" strokeWidth="0.4" />

            {/* Laurel halves */}
            {[1, -1].map((dir) => (
              <g key={dir} transform={`translate(60 60) scale(${dir} 1)`}>
                <path
                  d="M -32 -2 C -36 -18 -30 -34 -16 -42"
                  fill="none"
                  stroke={accent}
                  strokeOpacity="0.85"
                  strokeWidth="1"
                  strokeLinecap="round"
                />
                {/* Leaves */}
                {[-38, -28, -18, -8, 2, 12, 22].map((t, i) => {
                  const px = -32 + Math.cos((t + 90) * Math.PI / 180) * 2;
                  const py = t;
                  return (
                    <ellipse
                      key={i}
                      cx={-30 - i * 0.4}
                      cy={py}
                      rx="5"
                      ry="2"
                      transform={`rotate(${-30 + i * 8} ${-30 - i * 0.4} ${py})`}
                      fill={accent}
                      fillOpacity={0.55}
                    />
                  );
                })}
              </g>
            ))}

            {/* Central diamond */}
            <g transform="translate(60 60)">
              <path
                d="M0 -8 L6 0 L0 8 L-6 0 Z"
                fill={accent}
                fillOpacity="0.85"
              />
              <circle r="1.6" fill={bg} />
            </g>
          </svg>
        </div>

        {/* Names */}
        <div
          style={{
            fontFamily: font,
            fontSize: 30,
            lineHeight: 1.05,
            fontWeight: 400,
            color: ink,
          }}
        >
          {names}
        </div>

        <div style={{ flex: 1 }} />

        {/* Bottom caption */}
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6.5,
            fontWeight: 700,
            letterSpacing: "0.32em",
            color: accent,
            marginTop: "4%",
          }}
        >
          TAKLIFNOMA · {date}
        </div>
        <div
          style={{
            fontFamily: "var(--font-ui)",
            fontSize: 6,
            fontWeight: 600,
            letterSpacing: "0.28em",
            color: `${ink}99`,
            marginTop: 6,
          }}
        >
          {venue.toUpperCase()}
        </div>
      </div>
    </div>
  );
}
